import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { SettingsTitleWithTooltip } from 'content/pages/SettingsPage/helpers';
import PhaseAssignmentText from 'content/pages/SettingsPage/subpages/LoadBalancingSettings/components/PhaseAssignmentText';
import type { SetupLoadBalancingProps } from 'content/pages/SetupDevicesPage/SetupLoadBalancing';
import { useTranslation } from 'next-i18next';
import type { ChangeEvent, FC, SyntheticEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import Button from 'components/Button';
import CustomListItem from 'components/DeviceConfigComponents/CustomListItem';
import CopyIcon from 'components/Icons/CopyIcon';
import CustomRadio from 'components/MuiCustom/CustomCheckbox';
import CustomFormControl from 'components/MuiCustom/CustomFormControl';
import FilledTextField from 'components/MuiCustom/FilledTextField';

import { phaseListConstants } from 'redux-store/slices/api/types';

type EnabledOption = 'enable' | 'disable';
const ENABLED_OPTIONS: EnabledOption[] = ['enable', 'disable'];

interface CreateLoadBalancingGroupProps extends SetupLoadBalancingProps {
  submitLabel?: string;
  isEdit?: boolean;
}

const CreateLoadBalancingGroup: FC<CreateLoadBalancingGroupProps> = ({
  loadBalancingGroupId,
  // setLoadBalancingGroupId,
  loadBalancingEnabled,
  setLoadBalancingEnabledChange,
  maxTotalCurrent,
  setMaxTotalCurrent,
  maxGridCurrent,
  setMaxGridCurrent,
  loadBalancingPriority,
  setLoadBalancingPriority,
  loadBalancingFallbackCurrent,
  setLoadBalancingFallbackCurrent,
  phaseAssignment,
  setPhaseAssignment,
  isGroupConfig,
  setInputErrors,
  handleSubmitLoadBalancingSetup,
  submitLabel,
  isEdit = false,
}) => {
  const { t } = useTranslation();

  // TODO: Enable adding the name to the load balancing ID once the app implements the same logic.
  // const {
  //   id: groupId,
  //   // name: initialName
  // } = splitGroupId(loadBalancingGroupId);

  // const [groupName, setGroupName] = useState(initialName);

  const [errorMaxTotalCurrent, setErrorMaxTotalCurrent] = useState(false);
  const [errorMaxGridCurrent, setErrorMaxGridCurrent] = useState(false);
  const [errorLoadBalancingPriority, setErrorLoadBalancingPriority] =
    useState(false);
  const [
    errorLoadBalancingFallbackCurrent,
    setErrorLoadBalancingFallbackCurrent,
  ] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => () => clearTimeout(copyTimeout.current), []);

  // const handleGroupName = (e: ChangeEvent<HTMLInputElement>): void => {
  //   const name = e.target.value;
  //   const trimmed = name.trim();

  //   setGroupName(name);
  //   setLoadBalancingGroupId(trimmed ? `${groupId}-${trimmed}` : groupId);
  // };

  const handlePhaseChange = (event: SelectChangeEvent): void => {
    setPhaseAssignment?.(event.target.value);
  };

  const handleMaxTotalCurrent = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.target.value);

    setErrorMaxTotalCurrent(Number.isNaN(value) || value < 6);
    setMaxTotalCurrent(value);
  };

  const handleMaxGridCurrent = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.target.value);

    setErrorMaxGridCurrent(Number.isNaN(value) || value < 0);
    setMaxGridCurrent(value);
  };

  const handleLoadBalancingPriority = (
    e: ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = parseFloat(e.target.value);

    setErrorLoadBalancingPriority(value < 1 || value > 99);
    setLoadBalancingPriority?.(value);
  };

  const handleLoadBalancingFallbackCurrent = (
    e: ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = parseFloat(e.target.value);

    setErrorLoadBalancingFallbackCurrent(value < 0 || value > 32);
    setLoadBalancingFallbackCurrent?.(value);
  };

  const inputErrors =
    errorMaxTotalCurrent ||
    errorMaxGridCurrent ||
    errorLoadBalancingPriority ||
    errorLoadBalancingFallbackCurrent;

  useEffect(() => {
    setInputErrors(inputErrors);
  }, [inputErrors, setInputErrors]);

  const hasInvalidValues =
    inputErrors ||
    Number.isNaN(maxTotalCurrent) ||
    Number.isNaN(maxGridCurrent);

  const handleSubmit = (e: SyntheticEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (hasInvalidValues) return;

    handleSubmitLoadBalancingSetup(e);
  };

  const ampAdornment = (
    <InputAdornment position="end">
      {t('common:ampere_long').toLocaleLowerCase()}
    </InputAdornment>
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Box mb={4}>
        <Typography mb={1} ml={1.5} variant="h4" display="flex">
          {t('common:pages.load_balancing.page_title')}
        </Typography>
        {ENABLED_OPTIONS.map((option, index, { length }) => (
          <CustomListItem
            key={option}
            leftText={t(
              `common:pages.load_balancing.add_load_balancing_group.${option}_all_devices`,
            )}
            onClick={() => setLoadBalancingEnabledChange(option === 'enable')}
            leftComponent={
              <CustomRadio
                checked={loadBalancingEnabled === (option === 'enable')}
                sx={{ p: 0 }}
              />
            }
            isFirstItem={index === 0}
            isLastItem={index === length - 1}
          />
        ))}
      </Box>

      <Box mb={2} mt={4}>
        <CustomListItem
          leftText={t('common:pages.settings.subpages.load_balancing.group_id')}
          isSingleItem
          rightText={loadBalancingGroupId}
        />
      </Box>
      {/* <Box mb={1}>
        <FilledTextField
          label="Group name"
          name="groupName"
          type="text"
          value={groupName}
          onChange={handleGroupName}
          fullWidth
        />
      </Box> */}
      {isEdit ? (
        <CopyToClipboard
          text={loadBalancingGroupId}
          options={{ format: 'text/plain' }}
          onCopy={(_, result) => {
            if (!result) return;
            setCopied(true);
            clearTimeout(copyTimeout.current);
            copyTimeout.current = setTimeout(() => setCopied(false), 2000);
          }}
        >
          <Button variant="text">
            <CopyIcon /> {copied ? 'Copied!' : 'Copy Group ID'}
          </Button>
        </CopyToClipboard>
      ) : null}

      <Box mb={4} mt={4}>
        <SettingsTitleWithTooltip
          title={t(
            'common:pages.device_setup.load_balancing_setup.dynamic_load_balancing',
          )}
          tooltip={t(
            'common:pages.device_setup.load_balancing_setup.dynamic_load_balancing_desc',
          )}
        />
        <FilledTextField
          label={t(
            'common:pages.settings.subpages.load_balancing.maximum_grid_current',
          )}
          name="maxGridCurrent"
          type="number"
          value={maxGridCurrent}
          onChange={handleMaxGridCurrent}
          error={errorMaxGridCurrent}
          helperText={
            errorMaxGridCurrent
              ? t(
                  'common:pages.device_setup.load_balancing_setup.min_value_error',
                  { min: 0 },
                )
              : ''
          }
          fullWidth
          InputProps={{ endAdornment: ampAdornment, inputProps: { min: 0 } }}
        />
      </Box>

      <Box mb={4} mt={4}>
        <SettingsTitleWithTooltip
          title={t(
            'common:pages.device_setup.load_balancing_setup.static_load_balancing',
          )}
          tooltip={t(
            'common:pages.device_setup.load_balancing_setup.static_load_balancing_desc',
          )}
        />
        <FilledTextField
          label={t(
            'common:pages.settings.subpages.load_balancing.maximum_total_current',
          )}
          name="maxTotalCurrent"
          type="number"
          value={maxTotalCurrent}
          onChange={handleMaxTotalCurrent}
          error={errorMaxTotalCurrent}
          helperText={
            errorMaxTotalCurrent
              ? t(
                  'common:pages.device_setup.load_balancing_setup.min_value_error',
                  { min: 6 },
                )
              : ''
          }
          fullWidth
          InputProps={{ endAdornment: ampAdornment, inputProps: { min: 6 } }}
        />
      </Box>

      {isGroupConfig ? null : (
        <>
          <Box mb={2}>
            <FilledTextField
              label={t(
                'common:pages.settings.subpages.load_balancing.priority',
              )}
              name="loadBalancingPriority"
              type="number"
              inputProps={{ min: 0, max: 99 }}
              value={loadBalancingPriority}
              error={errorLoadBalancingPriority}
              onChange={handleLoadBalancingPriority}
              helperText={
                errorLoadBalancingPriority
                  ? t(
                      'common:pages.device_setup.load_balancing_setup.min_max_value_error',
                      { min: 0, max: 99 },
                    )
                  : ''
              }
              fullWidth
            />
          </Box>
          <Box mb={2}>
            <FilledTextField
              label={t(
                'common:pages.settings.subpages.load_balancing.maximum_fallback_current',
              )}
              name="loadBalancingFallbackCurrent"
              type="number"
              value={loadBalancingFallbackCurrent}
              error={errorLoadBalancingFallbackCurrent}
              onChange={handleLoadBalancingFallbackCurrent}
              helperText={
                errorLoadBalancingFallbackCurrent
                  ? t(
                      'common:pages.device_setup.load_balancing_setup.min_max_value_error',
                      { min: 6, max: 32 },
                    )
                  : ''
              }
              fullWidth
              InputProps={{
                endAdornment: ampAdornment,
                inputProps: {
                  min: 6,
                  max: 32,
                },
              }}
            />
          </Box>
          <Box mb={4}>
            <CustomFormControl fullWidth>
              <InputLabel>
                {t(
                  'common:pages.settings.subpages.load_balancing.phase_assignment',
                )}
              </InputLabel>
              <Select
                labelId="phaseAssignment-select-label"
                id="phaseAssignment"
                value={phaseAssignment}
                defaultValue={phaseAssignment}
                onChange={handlePhaseChange}
                IconComponent={prop => (
                  <UnfoldMoreRoundedIcon {...prop} fontSize="small" />
                )}
              >
                {phaseListConstants.map(phase => (
                  <MenuItem key={phase.toString()} value={phase.toString()}>
                    <PhaseAssignmentText
                      phaseAssignment={phase}
                      alginText="left"
                    />
                  </MenuItem>
                ))}
              </Select>
            </CustomFormControl>
          </Box>
        </>
      )}

      {submitLabel ? (
        <Box mb={2} mt={4}>
          <Button
            fullWidth
            variant="flat"
            color="primary"
            type="submit"
            size="large"
            disabled={hasInvalidValues}
            data-testid="submit"
          >
            {submitLabel}
          </Button>
        </Box>
      ) : null}
    </form>
  );
};

export default CreateLoadBalancingGroup;
